import { useEffect, type ReactNode } from 'react'
import { useSearchParams } from 'react-router'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/useUIStore'
import { useInboxUnreadCount } from '@/api/inbox'
import { DesktopSidebar } from './DesktopSidebar'
import { VoicemailMiniPlayer } from './VoicemailMiniPlayer'
import { GlobalCallModal } from './GlobalCallModal'
import { InCallWidget } from './InCallWidget'
import { TransferCallModal } from './TransferCallModal'
import { MobileBrowserSplash } from '@/components/MobileBrowserSplash'
import { InactiveTabOverlay } from './InactiveTabOverlay'
import { NouveauteBoite } from './NouveauteBoite'
import { useToastMentions } from '@/hooks/useToastMentions'
import { OutboundSuspendedBanner } from '@/components/manager/OutboundSuspendedBanner'

interface Props {
  children: ReactNode
}

export function DashboardLayout({ children }: Props) {
  const [searchParams, setSearchParams] = useSearchParams()
  const darkMode = useUIStore((s) => s.darkMode)
  const setSelectedInboxPhone = useUIStore((s) => s.setSelectedInboxPhone)
  const setSelectedCollegue = useUIStore((s) => s.setSelectedCollegue)
  const setInboxFiltreDemande = useUIStore((s) => s.setInboxFiltreDemande)
  const setInboxLineFilter = useUIStore((s) => s.setInboxLineFilter)
  // Mentions reçues pendant l'absence : un toast par session.
  useToastMentions()
  // Conversations non lues (textos, vocaux et manqués compris).
  const unreadInboxCount = useInboxUnreadCount()

  // L'ancienne vue SMS est servie par la Boîte de réception.
  const view = searchParams.get('view') || 'phone'
  const activeTab = view === 'sms' ? 'phone' : view

  const handleTabChange = (tab: string) => {
    const next = new URLSearchParams(searchParams)
    if (tab === 'phone') next.delete('view')
    else next.set('view', tab)
    setSearchParams(next, { replace: true })
  }

  // Synchronise la classe `dark` sur <html> avec useUIStore.darkMode
  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode)
  }, [darkMode])

  // Écoute l'event d'ouverture de conversation (appel accepté, fiche contact,
  // toast) : ouvre le fil de la personne dans la Boîte de réception.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ phone: string; line?: string | null }>).detail
      if (!detail?.phone) return
      const next = new URLSearchParams(window.location.search)
      next.delete('view')
      next.delete('tab')
      setSearchParams(next, { replace: true })
      setSelectedInboxPhone(detail.phone, detail.line ?? null)
    }
    window.addEventListener('callizy:open-conversation', handler)
    return () => window.removeEventListener('callizy:open-conversation', handler)
  }, [setSelectedInboxPhone, setSearchParams])

  // Même chose pour le fil de messages d'un collègue (barre latérale, toast).
  useEffect(() => {
    const handler = (e: Event) => {
      const userId = (e as CustomEvent<{ userId: number }>).detail?.userId
      if (!userId) return
      const next = new URLSearchParams(window.location.search)
      next.delete('view')
      next.delete('tab')
      setSearchParams(next, { replace: true })
      setSelectedCollegue(userId)
    }
    window.addEventListener('callizy:open-colleague', handler)
    return () => window.removeEventListener('callizy:open-colleague', handler)
  }, [setSelectedCollegue, setSearchParams])

  // « Voir » du toast des mentions : la boîte, toutes les lignes, filtre Mentions.
  useEffect(() => {
    const handler = () => {
      const next = new URLSearchParams(window.location.search)
      next.delete('view')
      next.delete('tab')
      setSearchParams(next, { replace: true })
      setInboxLineFilter(null)
      setInboxFiltreDemande('mentions')
    }
    window.addEventListener('callizy:open-mentions', handler)
    return () => window.removeEventListener('callizy:open-mentions', handler)
  }, [setInboxFiltreDemande, setInboxLineFilter, setSearchParams])

  return (
    <div
      className={cn(
        'h-screen flex overflow-hidden',
        darkMode ? 'bg-slate-900 text-slate-100' : 'bg-slate-50 text-slate-900',
      )}
    >
      <DesktopSidebar
        activeTab={activeTab}
        onTabChange={handleTabChange}
        unreadInboxCount={unreadInboxCount}
      />

      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        {/* Bannière : sortant en retard/suspendu pour impayé (ne s'affiche que si concerné) */}
        <OutboundSuspendedBanner />
        <main className="flex-1 min-h-0 overflow-hidden">{children}</main>
      </div>

      <VoicemailMiniPlayer />
      <GlobalCallModal />
      {/* Widget flottant — apparaît en pilule quand l'agent quitte la page
          contact actif. L'InCallPanel inline set hasInlineCallUI=true sur la
          timeline → le widget se masque automatiquement pour éviter le doublon. */}
      <InCallWidget defaultMinimized />
      <TransferCallModal />
      <MobileBrowserSplash />
      <InactiveTabOverlay />
      <NouveauteBoite onOuvrir={() => handleTabChange('phone')} />
    </div>
  )
}
